import { FC, useState, useCallback, useContext, useRef, Fragment } from "react";
import {
  styled,
  Typography,
  Stack,
  Chip,
  IconButton,
  Checkbox,
  Card,
  CardActionArea,
  Link,
  Slide,
  Fade,
  useTheme,
  Skeleton,
} from "@mui/material";
import { useNavigate } from "react-router";
import { Link as RouterLink } from "react-router";

import GraphicEqOutlinedIcon from "@mui/icons-material/GraphicEqOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import TvOutlinedIcon from "@mui/icons-material/TvOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import BookmarkBorderOutlinedIcon from "@mui/icons-material/BookmarkBorderOutlined";
import BookmarkOutlinedIcon from "@mui/icons-material/BookmarkOutlined";
import useCountMaxTags from "../../../Utils/Hooks/useCountMaxTags";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import VideoCameraBackIcon from "@mui/icons-material/VideoCameraBack";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import CheckboxBg from "../../Misc/CheckboxBg";
import { assetService } from "../../../Core/Api/services";
import { GetTitleRouterPath, UserHasAcl, UserHasLicence } from "../../../Utils/helpers";
import AssetVideoResolutionChip from "../../Misc/Chips/AssetChip/asset-video-resolution-chip";
import useCollectionActions from "../../../Utils/Hooks/useCollectionActions";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import useBasketActions from "../../../Utils/Hooks/useBasketActions";
import { ModalContext } from "../../Modals";
import ModalCreateShare from "../../Modals/ModalCreateShare/modal-create-share";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { Licences } from "../../../Core/Api/Types/licences";
import { routePaths } from "../../../Router/router";
import { useTranslation } from "react-i18next";

export type MovieCardProps = {
  id: number;
  obj?: DbAssetObjectInfos;
  cover: string;
  title: string;
  labelList: string[];
  transparentChipText?: string;
  favorite: boolean;
  selectMode?: boolean;
  shareBtn?: boolean;
  orderBtn?: boolean;
  collectionBtn?: boolean;
  selected?: boolean;
  selectable?: boolean;
  selectedOnClick?: boolean;
  onSelectClick?: (obj: DbAssetObjectInfos) => void;
  expand: {
    tagList: string[];
    contentList: string[];
    audioList: string[];
    subtitleList: string[];
    quality: string;
  };
  noHover?: boolean;
  isDrive?: boolean;
};

const MovieCard: FC<MovieCardProps> = (props) => {
  const { t } = useTranslation();
  const collectionActions = useCollectionActions();
  const [subtitles, setSubtitles] = useState<string[]>([]);
  const [audios, setAudios] = useState<string[]>([]);
  const [qualities, setQualities] = useState<string[]>([]);
  const [oar, setOar] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [isFetched, setFetched] = useState(false);
  const [hover, setHover] = useState(false);

  const {
    id,
    obj,
    selected,
    onSelectClick,
    expand,
    selectedOnClick,
    shareBtn,
    orderBtn,
    collectionBtn,
    selectable = true,
    isDrive = false,
  } = props;
  const { visibleTags, hiddenTagsCount } = useCountMaxTags({
    tagList: expand.tagList,
  });

  const navigate = useNavigate();
  const theme = useTheme();
  const movieCardRef = useRef<HTMLDivElement>(null);

  const modalContext = useContext(ModalContext);
  const { openModal } = { ...modalContext };

  // Hooks
  const { addObjectByIdsToBasket } = useBasketActions();

  const handleCheckboxClick: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    (e) => {
      e.stopPropagation();

      if (!onSelectClick || !obj) return;

      onSelectClick(obj);
    },
    [onSelectClick, obj]
  );

  const handleCardOver = useCallback(() => {
    if (isFetched) return;

    //e.preventDefault();
    setIsLoading(true);

    assetService.GetGraph(id, true, true, true).then(function (response) {
      if (response && response.graph && response.graph.metadata) {
        setSubtitles(
          response.graph.metadata.find((m) => m.fieldName === "allSubtitleLanguages")?.value.map((v) => v.label) || []
        );
        setAudios(
          response.graph.metadata.find((m) => m.fieldName === "allAudioLanguages")?.value.map((v) => v.label) || []
        );
        setQualities(response.graph.metadata.find((m) => m.fieldName === "allVideoQualities")?.value || []);
        setOar(response.graph.metadata.find((m) => m.fieldName === "titleOriginalPictureFormat")?.value);
        setIsLoading(false);
        setFetched(true);
      }
    });
  }, [isFetched, id]);

  const handleCardClick: React.MouseEventHandler = useCallback(
    (e) => {
      if ((selectedOnClick || e.ctrlKey) && onSelectClick && obj) {
        onSelectClick(obj);
      } else {
        navigate((isDrive ? routePaths.drive.path : GetTitleRouterPath()) + "/" + id);

        if (onSelectClick && obj) onSelectClick(obj);
      }
    },
    [onSelectClick, navigate, selectedOnClick, obj]
  );

  const handleAddToCollectionModal: React.MouseEventHandler = useCallback(
    (e) => {
      e.stopPropagation();
      collectionActions.AddItemsToCollection([new DbAssetObjectInfos({ id: id, name: props.title })]);
    },
    [id]
  );

  const handleShare: React.MouseEventHandler = useCallback(
    (e) => {
      e.stopPropagation();

      if (!openModal) return;

      assetService.GetObjectLightById(id, 0).then((response) => {
        openModal(ModalCreateShare, { object: response.graph });
      });
    },
    [id]
  );

  const handleOrder: React.MouseEventHandler = useCallback(
    (e) => {
      e.stopPropagation();
      addObjectByIdsToBasket([id]);
    },
    [id]
  );

  const handleCollection: React.MouseEventHandler = useCallback(
    (e) => {
      e.stopPropagation();
      collectionActions.AddItemByIdToCollection(id);
    },
    [id]
  );

  return (
    <StyledLink
      className="MuiMovieCard"
      selected={selected}
      hover={hover}
      onMouseEnter={(e) => !props.noHover && setHover(true)}
      onMouseLeave={(e) => !props.noHover && setHover(false)}
      ref={movieCardRef}
    >
      <CardActionArea onClick={handleCardClick} sx={{ flexDirection: "column", justifyContent: "space-between" }}>
        <Fade
          in={hover || selected}
          timeout={theme.transitions.duration.standard}
          easing={theme.transitions.easing.easeOut}
        >
          <CardTop>
            {props.selectMode ? (
              <>
                {selectable && (
                  <>
                    <CheckboxBg
                      checked={props.selected || false}
                      className="MuiCheckbox-Bg"
                      sx={{ top: "17px", left: "17px" }}
                    />
                    <Checkbox checked={props.selected || false} onClick={handleCheckboxClick}></Checkbox>
                  </>
                )}
              </>
            ) : (
              <>
                {selectable && (
                  <IconButton
                    sx={{
                      ml: 1,
                      mt: 1,
                      background: theme.palette.background.paper,
                      "&:hover": { background: theme.palette.background[200] },
                    }}
                    aria-label="favorite"
                    component="label"
                    onClick={handleAddToCollectionModal}
                    color="neutral"
                    size="small"
                  >
                    {props.favorite ? (
                      <BookmarkOutlinedIcon fontSize="small" className="action-icon" />
                    ) : (
                      <BookmarkBorderOutlinedIcon fontSize="small" className="action-icon" />
                    )}
                  </IconButton>
                )}
              </>
            )}
            <Stack gap={0.5} direction="row" p={theme.spacing(1)}>
              {shareBtn &&
                shareBtn === true &&
                UserHasAcl(AclTypes.aclShareCreate) &&
                UserHasLicence(Licences.ShareScreening) && (
                  <IconButton
                    aria-label="share"
                    component="label"
                    onClick={handleShare}
                    size="small"
                    color="neutral"
                    className="action-icon"
                    sx={{
                      background: theme.palette.background.paper,
                      "&:hover": { background: theme.palette.background[200] },
                    }}
                  >
                    <ShareOutlinedIcon fontSize="small" className="action-icon" />
                  </IconButton>
                )}
              {orderBtn && orderBtn === true && UserHasAcl(AclTypes.aclSectionOrderAccess) && (
                <IconButton
                  aria-label="order"
                  component="label"
                  onClick={handleOrder}
                  size="small"
                  color="neutral"
                  className="action-icon"
                  sx={{
                    background: theme.palette.background.paper,
                    "&:hover": { background: theme.palette.background[200] },
                  }}
                >
                  <ShoppingBagOutlinedIcon fontSize="small" className="action-icon" />
                </IconButton>
              )}
              {collectionBtn && collectionBtn === true && (
                <IconButton
                  aria-label="add to collection"
                  component="label"
                  onClick={handleCollection}
                  size="small"
                  color="neutral"
                  className="action-icon"
                  sx={{
                    background: theme.palette.background.paper,
                    "&:hover": { background: theme.palette.background[200] },
                  }}
                >
                  <BookmarkBorderIcon fontSize="small" className="action-icon" />
                </IconButton>
              )}
            </Stack>
          </CardTop>
        </Fade>
        {(props.noHover === undefined || props.noHover === false) && (
          <Slide
            in={hover}
            direction="up"
            timeout={theme.transitions.duration.standard}
            easing={theme.transitions.easing.easeOut}
            container={movieCardRef.current}
          >
            <CardExpand className="movie-card-footer">
              <Link
                underline="hover"
                color="text.primary"
                component={RouterLink}
                fontWeight={600}
                to={`${GetTitleRouterPath()}/${id}`}
                sx={{ pointerEvents: "auto", cursor: "pointer !important" }}
              >
                <Typography component="h3" variant="subtitle2" fontWeight={500}>
                  {props.title}
                </Typography>
              </Link>
              <Stack direction="row" flexWrap="wrap" spacing={0.5}>
                {visibleTags.map((tag) => (
                  <Chip color="primary" size="small" variant="outlined" label={tag} key={`${id}-${tag}`} />
                ))}
                {hiddenTagsCount > 0 && (
                  <Chip
                    color="primary"
                    size="small"
                    variant="outlined"
                    label={`+${hiddenTagsCount}`}
                    key={`${id}-tagmore`}
                  />
                )}
              </Stack>

              {isLoading ? (
                <Stack gap={0.5}>
                  <Skeleton width={100} height={20} />
                  <Skeleton width={100} height={20} />
                  <Skeleton width={100} height={20} />
                  <Skeleton width={100} height={20} />
                </Stack>
              ) : (
                <Stack direction="column">
                  <Stack direction="row" gap={1}>
                    <GraphicEqOutlinedIcon fontSize="small" sx={{ color: "text.tier" }} />
                    <Stack direction="row" flexWrap="wrap" alignItems="center" gap={0.5} sx={{ mb: 1 }}>
                      {audios.length === 0 && (
                        <Typography component="span" variant="body2" fontWeight={500} sx={{ color: "text.secondary" }}>
                          {"N/A"}
                        </Typography>
                      )}

                      {audios.length > 0 &&
                        audios.slice(0, 5).map((audio, index) => (
                          <Fragment key={`key-${audio}-${index}`}>
                            <Typography
                              component="span"
                              variant="body2"
                              color="text.secondary"
                              fontWeight={500}
                              key={`${id}-a${index}`}
                              sx={{ color: "text.secondary" }}
                            >
                              {audio}
                              {index !== audios.length - 1 && ","}
                            </Typography>
                            {index === 4 && audios.length - 5 > 0 && (
                              <Typography
                                component="span"
                                variant="body2"
                                color="text.secondary"
                                fontWeight={500}
                                key={`${id}-a${index}-4a`}
                                sx={{ color: "text.tier" }}
                              >
                                +{audios.length - 5}
                              </Typography>
                            )}
                          </Fragment>
                        ))}
                    </Stack>
                  </Stack>

                  <Stack direction="row" gap={1}>
                    <ChatOutlinedIcon fontSize="small" sx={{ color: "text.tier" }} />
                    <Stack direction="row" flexWrap="wrap" alignItems="center" gap={0.5} sx={{ mb: 1 }}>
                      {subtitles.length === 0 && (
                        <Typography component="span" variant="body2" fontWeight={500}>
                          {"N/A"}
                        </Typography>
                      )}
                      {subtitles.length > 0 &&
                        subtitles.slice(0, 5).map((subtitle, index) => (
                          <Fragment key={`key-${subtitle}-${index}`}>
                            <Typography
                              component="span"
                              variant="body2"
                              color="text.secondary"
                              fontWeight={500}
                              key={`${id}-s${index}`}
                              sx={{ color: "text.secondary" }}
                            >
                              {subtitle}
                              {index !== subtitles.length - 1 && ","}
                            </Typography>
                            {index === 4 && subtitles.length - 5 > 0 && (
                              <Typography
                                component="span"
                                variant="body2"
                                color="text.secondary"
                                fontWeight={500}
                                key={`${id}-s${index}-4s`}
                                sx={{ color: "text.tier" }}
                              >
                                +{subtitles.length - 5}
                              </Typography>
                            )}
                          </Fragment>
                        ))}
                    </Stack>
                  </Stack>

                  <Stack direction="row" gap={1}>
                    <TvOutlinedIcon fontSize="small" sx={{ color: "text.tier" }} />
                    <Stack direction="row" flexWrap="wrap" alignItems="center" gap={0.5} sx={{ mb: 1 }}>
                      {qualities.length === 0 && (
                        <Typography component="span" variant="body2" fontWeight={500}>
                          {"N/A"}
                        </Typography>
                      )}
                      {qualities.length > 0 &&
                        qualities.map((quality, index) => (
                          <AssetVideoResolutionChip
                            key={`${id}-q${index}`}
                            label={quality}
                            resolution={quality}
                            size="small"
                          />
                        ))}
                    </Stack>
                  </Stack>

                  <Stack direction="row" gap={1}>
                    <VideoCameraBackIcon fontSize="small" sx={{ color: "text.tier" }} />
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography component="span" variant="body2" fontWeight={500}>
                        {!oar || oar === "" ? "N/A" : oar}
                      </Typography>
                    </Stack>
                  </Stack>
                </Stack>
              )}
            </CardExpand>
          </Slide>
        )}
        <CardBody
          onMouseOver={(e) => {
            if (props.noHover) return;

            e.stopPropagation();
            e.preventDefault();
            handleCardOver();
          }}
          hover={hover}
        >
          <figure style={{ position: "relative", flexGrow: 1 }}>
            {props.cover && <img src={props.cover} alt={props.title} loading="lazy" />}
            {props.transparentChipText && (
              <Chip
                size="small"
                variant="filled"
                color="default"
                label={props.transparentChipText}
                sx={(theme) => ({
                  position: "absolute",
                  bottom: theme.spacing(1),
                  left: theme.spacing(1),
                  // backdropFilter: "blur(12px)",
                  // mixBlendMode: "difference",
                })}
              />
            )}
            <Fade in={!(hover || selected)}>
              <Stack spacing={0.5} sx={{ position: "absolute", top: 8, right: 0 }}>
                {props.labelList.map((label, index) => (
                  <StyledLabel
                    type={label === "NEW" ? "premium" : label === "DELETED" ? "top" : "new"}
                    key={`${id}-label-${index}`}
                    label={
                      label === "premium" ? (
                        <AutoAwesomeOutlinedIcon sx={{ fontSize: 14, position: "relative", top: 2 }} />
                      ) : (
                        label
                      )
                    }
                    size="small"
                  />
                ))}
              </Stack>
            </Fade>
          </figure>
          <Typography
            component="h3"
            variant="subtitle2"
            fontWeight={500}
            sx={(theme) => ({ ...theme.helpers(theme).clampedText })}
          >
            {props.title}
          </Typography>
        </CardBody>
      </CardActionArea>
    </StyledLink>
  );
};

export default MovieCard;

export const StyledLink = styled(Card, {
  shouldForwardProp: (prop) => prop !== "selected" && prop !== "hover",
})<{ selected?: boolean; hover: boolean }>(({ theme, selected, hover }) => ({
  position: "relative",
  display: "block",
  border: `1px solid ${theme.palette.backgroundAlpha.border}`,
  background: "none",
  borderRadius: 6,
  overflow: "hidden",
  color: theme.palette.text.primary,
  boxSizing: "border-box",
  aspectRatio: "2 / 3",
  boxShadow: hover ? theme.shadows[4] : "none",
  transition: `all ${theme.transitions.duration.standard}ms ${theme.transitions.easing.sharp}`,

  ".MuiCardActionArea-root": {
    display: "inline-flex",
    alignItems: "flex-start",
    height: "100%",
  },
  ".MuiCardActionArea-focusHighlight": {
    display: "none",
  },
  "&:hover": {
    background: theme.palette.background.alphaHover,
    transform: "scale(1.05)",

    ".MuiCheckbox-Bg": {
      opacity: 0.5,
    },
  },

  ...(selected && {
    borderColor: theme.palette.primary.main,
    borderWidth: 2,
  }),
}));

export const CardTop = styled("div")(({ theme }) => ({
  padding: theme.spacing(1),
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  zIndex: 1,
  width: "100%",
}));

export const CardTopLeft = styled(Stack)(({ theme }) => ({ width: "100%" }));

export const StyledLabel = styled(Chip, { shouldForwardProp: (prop) => prop !== "type" })<{
  type?: string | "new" | "top" | "premium";
}>(({ theme, type }) => {
  let background = "#011181c4";
  switch (type) {
    case "top":
      background = theme.palette.success.main;
      break;
    case "premium":
      background = theme.palette.info.main;
      break;
  }

  return {
    background: background,
    color: theme.palette.text.inverted,
    borderRadius: 0,
    textTransform: "uppercase",
  };
});

export const CardBody = styled("div", { shouldForwardProp: (prop) => prop !== "hover" })<{ hover: boolean }>(
  ({ theme, hover }) => ({
    padding: theme.spacing(1),
    width: "100%",
    height: "100%",
    zIndex: 0,
    position: "absolute",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    overflow: "hidden",
    ...(hover && {
      maskImage: "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,.3) 40%, rgba(0,0,0,0) 100%)",
    }),
    transition: `all ${theme.transitions.duration.standard}ms ${theme.transitions.easing.sharp}`,
    figure: {
      position: "relative",
      flexGrow: 1,
      width: "100%",
      //   paddingBottom: `${(192 / 143) * 100}%`,
      borderRadius: 3,
      overflow: "hidden",

      img: {
        objectFit: "cover",
        objectPosition: "top",
        transform: hover ? "scale(1.1)" : "scale(1)",
        filter: hover ? "blur(2px)" : "blur(0)",
        // filter: hover ? "brightness(0.5)" : "brightness(1)",
        transition: `all ${theme.transitions.duration.standard}ms ${theme.transitions.easing.sharp}`,
      },
    },

    h3: {
      paddingLeft: theme.spacing(0.5),
      paddingRight: theme.spacing(0.5),
    },

    img: {
      position: "absolute",
      height: "100%",
      width: "100%",
    },
  })
);

export const CardExpand = styled("div")(({ theme }) => ({
  //   background: theme.palette.background.default,
  padding: theme.spacing(1.5),
  color: theme.palette.text.secondary,
  zIndex: 1,
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
}));

// export const StyledTitleTypography = styled(Typography)(({ theme }) => ({
//   ...theme.helpers(theme).clampedText,
// })) as typeof Typography;

// export const StyledTitleTypography = styled(Typography, { shouldForwardProp: (prop) => prop !== "hover" })<{
//   hover: boolean;
// }>(({ theme, hover }) => ({ ...(!hover && { ...theme.helpers(theme).clampedText }) }));

export const MovieCardGrid = styled("div")(() => ({
  display: "grid",
  gridColumnGap: "16px",
  gridRowGap: "32px",
  //   gridTemplateColumns: "repeat(auto-fit, minmax(260px, 260px))", // Original value is 160px
  gridTemplateColumns: `repeat(6, minmax(0, 1fr))`,
  img: {
    width: "100%",
  },
}));
